import * as React from 'react';
import { YardiPropertyConfiguration } from '../../../../../api/PropertyIntegration';
import CollapsibleCard from '../../../../common/CollapsibleCard';
import CheckboxLabeled from '../../../../common/form/CheckboxLabeled';
import FormInput from '../../../../common/form/Input';
import TagTextField from '../../../../common/TagTextField';
import { userIsCompanyManageUser, userIsPropertyManageUser, userIsSystemManageUser } from '../../../../../utilities/identity';

interface Props {
    config?: YardiPropertyConfiguration;
    update: (newConfig?: YardiPropertyConfiguration) => void;
}

const YardiConfigurationSection = (props: Props) => {
    const isUser =
    userIsCompanyManageUser() ||
    userIsPropertyManageUser() ||
    userIsSystemManageUser();
    return(
    <CollapsibleCard
        cardTitle="Yardi Configuration"
        cardOpen={!!props.config}
        onCardOpenChange={open =>{
            if (isUser)  return;
            props.update(
                open
                    ? {
                          username: '',
                          password: '',
                          serverName: '',
                          database: '',
                          propertyId: '',
                          webServiceUrl: '',
                          modelUnitName: '',
                          sendModelUnitNameWithGuestCard: false,
                          modelUnitTypeId: '',
                          firstContactEventReason: '',
                          agent: '',
                          enableMultiPropertyConfig: false,
                          propertyIds: []
                      }
                    : undefined
            )
        }}
    >
        {props.config && (
            <div>
                <FormInput
                    labelProps={{
                        labelText: 'Username',
                        invalidText: 'Please enter the Yardi username'
                    }}
                    inputProps={{
                        id: 'yardi-username',
                        name: 'username',
                        value: props.config.username,
                        type: 'string',
                        required: true,
                        disabled: isUser 
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, username: value.toString() })
                    }
                />
                <FormInput
                    labelProps={{
                        labelText: 'Password',
                        invalidText: 'Please enter the Yardi password'
                    }}
                    inputProps={{
                        id: 'yardi-password',
                        name: 'password',
                        value: props.config.password,
                        type: 'string',
                        required: true,
                        disabled: isUser 
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, password: value.toString() })
                    }
                />
                <FormInput
                    labelProps={{
                        labelText: 'Server Name',
                        invalidText: 'Please enter the Yardi server name'
                    }}
                    inputProps={{
                        id: 'yardi-server-name',
                        name: 'serverName',
                        value: props.config.serverName,
                        type: 'string',
                        required: true,
                        disabled: isUser 
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, serverName: value.toString() })
                    }
                />
                <FormInput
                    labelProps={{
                        labelText: 'Database',
                        invalidText:
                            'Please enter the Yardi database (usually the same as the server name)'
                    }}
                    inputProps={{
                        id: 'yardi-database',
                        name: 'database',
                        value: props.config.database,
                        type: 'string',
                        required: true,
                        disabled: isUser 
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, database: value.toString() })
                    }
                />
                <div className="row">
                    <div className="col-6 font-weight-bold">{ props.config.enableMultiPropertyConfig ? "Property Ids" :"Property Id"}</div>
                    <div className="col-6 tour-config-section-yes-no-toggle-wrapper" style={{display:'flex', alignItems:'center', marginBottom:'15px'}}>
                        <div className="tour-enable-radio-btn-wrapper">    
                            <div className="custom-radio-wrapper">
                                <div                                
                                    className="custom-radio-circle"
                                    onClick={() =>{ if (!isUser) { props.update({ ...props.config!, enableMultiPropertyConfig: true, propertyId:'' });}
                                                  }}
                                >   
                                    {props.config.enableMultiPropertyConfig &&
                                        <div className="custom-radio-selected"/>
                                    }
                                </div>
                                <div className="custom-radio-label">Multi Property Ids</div>
                                <div
                                    className="custom-radio-circle"  
                                    onClick={() => { if (!isUser) { props.update({ ...props.config!, enableMultiPropertyConfig: false, propertyIds: [] });}   
                                }}                       
                                >   
                                    {!props.config.enableMultiPropertyConfig &&
                                        <div className="custom-radio-selected"/>                                
                                    }
                                </div>
                                <div className="custom-radio-label">Property Id</div>     
                            </div>                                      
                            
                        </div>
                    </div>
                </div>
                <TagTextField {...props}/>
                <FormInput
                    labelProps={{
                        labelText: 'Voyager URL',
                        invalidText: 'Please check URL format. Should match placeholder.'
                    }}
                    inputProps={{
                        id: 'yardi-voyager-url',
                        name: 'voyagerUrl',
                        value: props.config.webServiceUrl,
                        type: 'string',
                        required: true,
                        disabled: isUser,
                        placeholder:
                            'https://www.yardipcv.com/{your_ID}/webservices/itfilsguestcard.asmx',
                        pattern: 'https://www..+.com/.+/webservices/itfilsguestcard.asmx'
                    }}
                    helpTextProps={{
                        helpText: 'This shares the same base as your Voyager URL.'
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, webServiceUrl: value.toString() })
                    }
                />
                <FormInput
                    labelProps={{
                        labelText: 'Model Unit Name',
                        invalidText: 'Please enter the model unit name'
                    }}
                    inputProps={{
                        id: 'yardi-model-unit-name',
                        name: 'yardiModelUnitName',
                        value: props.config.modelUnitName,
                        type: 'string',
                        required: true,
                        disabled: isUser 
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, modelUnitName: value.toString() })
                    }
                />
                <CheckboxLabeled
                    labelProps={{ labelText: 'Send model unit name with Guest Card?' }}
                    inputProps={{
                        id: 'yardi-model-unit-name-send',
                        name: 'sendModelUnitNameWithGuestCard',
                        checked: props.config.sendModelUnitNameWithGuestCard,
                        disabled: isUser 
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, sendModelUnitNameWithGuestCard: value })
                    }
                />
                <FormInput
                    labelProps={{
                        labelText: 'Model Unit Type ID',
                        invalidText: 'Please enter the model unit type ID'
                    }}
                    inputProps={{
                        id: 'yardi-model-unit-type-id',
                        name: 'yardiModelUnitTypeId',
                        value: props.config.modelUnitTypeId,
                        type: 'string',
                        required: false,
                        disabled: isUser 
                    }}
                    helpTextProps={{
                        helpText: 'Optional model unit type to include when forwarding prospects.'
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({ ...props.config!, modelUnitTypeId: value.toString() })
                    }
                />
                <FormInput
                    labelProps={{
                        labelText: 'First Contact Event Reason'
                    }}
                    inputProps={{
                        id: 'yardi-first-contact-event-reason',
                        name: 'yardiFirstContactEventReason',
                        value: props.config.firstContactEventReason,
                        type: 'string',
                        required: false,
                        disabled: isUser 
                    }}
                    helpTextProps={{
                        helpText: 'Event Reason for First Contact Events (if required)'
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({
                            ...props.config!,
                            firstContactEventReason: value.toString()
                        })
                    }
                />
                <FormInput
                    labelProps={{
                        labelText: 'Agent',
                        invalidText:
                            'Please enter the agent as you would like it forwarded to Yardi'
                    }}
                    inputProps={{
                        id: 'yardi-agent',
                        name: 'yardiAgent',
                        value: props.config.agent,
                        type: 'string',
                        required: true,
                        disabled: isUser 
                    }}
                    helpTextProps={{
                        helpText: 'Default agent for Yardi prospects'
                    }}
                    onChange={(_name, value) =>
                        !isUser && props.update({
                            ...props.config!,
                            agent: value.toString()
                        })
                    }
                />
            </div>
        )}
    </CollapsibleCard>
);
};
export default YardiConfigurationSection;



// WEBPACK FOOTER //
// ./src/components/pages/admin/editProperty/integrationControls/YardiConfigurationSection.tsx